import React, { useEffect } from 'react';
import { UserRenderer } from 'apps/sigma2-ui/src/state/user';
import { ObservationWrapperWithKey } from 'apps/sigma2-ui/src/components/ObservationWrapperWithKey';
import { UserAnalysisSignaling } from 'apps/sigma2-ui/src/state/user-analysis-signalling';
import { OrgSwitcherWrapper } from 'apps/sigma2-ui/src/components/OrgSwitcherContext';
import { useNavigateSafe } from 'apps/sigma2-ui/src/lib/hooks/useNavigateSafe';
import { FilterStoreRenderer } from 'apps/sigma2-ui/src/filters-v2/components/FilterStoreRenderer';
import { APIMetaInformationRenderer } from 'apps/sigma2-ui/src/state/meta-information';
import { SavedAnalysisRenderer } from 'apps/sigma2-ui/src/filters-v2/lib/useSavedAnalysis';
import { ReportsRenderer } from 'apps/sigma2-ui/src/reports/hooks/ReportsRenderer';
import { DashboardsRenderer } from 'apps/sigma2-ui/src/lib/hooks/useDashboards';
import { LocalizationProvider } from '@mui/x-date-pickers-pro';
import { AdapterDateFns } from '@mui/x-date-pickers-pro/AdapterDateFns';
import { EmbedModeContext } from 'apps/sigma2-ui/src/components/EmbedModeContext';
import { AssistantProvider } from 'apps/sigma2-ui/src/lib/events/AssistantProvider';

export const AppWrapper = ({ children }: { children: React.ReactNode }) => {
  return (
    <UserRenderer>
      <APIMetaInformationRenderer>
        <OrgSwitcherWrapper>
          <FilterStoreRenderer>
            <ObservationWrapperWithKey>
              <UserAnalysisSignaling>
                <ReportsRenderer>
                  <SavedAnalysisRenderer>
                    <EmbedModeContext.Provider value={false}>
                      <DashboardsRenderer>
                        <AppWrapperLinkNavigator>
                          <LocalizationProvider dateAdapter={AdapterDateFns}>
                            <AssistantProvider>{children}</AssistantProvider>
                          </LocalizationProvider>
                        </AppWrapperLinkNavigator>
                      </DashboardsRenderer>
                    </EmbedModeContext.Provider>
                  </SavedAnalysisRenderer>
                </ReportsRenderer>
              </UserAnalysisSignaling>
            </ObservationWrapperWithKey>
          </FilterStoreRenderer>
        </OrgSwitcherWrapper>
      </APIMetaInformationRenderer>
    </UserRenderer>
  );
};

export const AppWrapperLinkNavigator = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  // We do this, because some components are rendered outside the router, and it doesn't make sense to prevent them from navigating safely.
  const navigateSafe = useNavigateSafe();

  useEffect(() => {
    const globalClickHandler = (e: MouseEvent) => {
      if (e.defaultPrevented) {
        return;
      }

      if (!e.target) {
        return;
      }
      if (!(e.target instanceof Element)) {
        return;
      }

      const origin = e.target.closest(`a`);

      if (
        !origin ||
        !origin.href ||
        !origin.href.startsWith(window.location.origin)
      ) {
        return;
      }

      // If the link has a `download` attribute, let the browser handle it
      if (origin.hasAttribute('download')) {
        return;
      }

      // Allow links with target="_blank" to open in a new tab
      if (origin.target === '_blank') {
        return;
      }

      const hash = origin.href.split('#')[1];

      if (origin.href.startsWith('mailto:')) {
        // let the browser handle mailto links
        return;
      }

      e.preventDefault();

      if (hash) {
        const element = document.getElementById(hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
          return;
        }
      }

      navigateSafe(origin.href);
    };

    document.addEventListener(`click`, globalClickHandler);

    return () => {
      document.removeEventListener(`click`, globalClickHandler);
    };
  }, [navigateSafe]);

  return children;
};
